/**
 * La consegna in una pagina sola (GI-237): un elenco di tappe, dalla verifica
 * alla ricevuta, con lo stato di ognuna e l'azione dentro la tappa.
 *
 * **Prima erano tre schede e un wizard.** Congelamento, campione e sigillo
 * stavano nel modulo qualita', poi un wizard di sette passi con un titolo per
 * passo e «Avanti» in fondo: chi doveva consegnare non vedeva mai la strada
 * intera, e non sapeva a che punto fosse. Qui la strada e' l'unica cosa che si
 * vede: che cosa e' fatto, che cosa tocca adesso, che cosa aspetta.
 *
 * **Una tappa attiva alla volta, e le altre si leggono.** L'azione sta nella
 * tappa che tocca; le tappe fatte dicono che cosa hanno fissato, quelle a
 * venire perche' aspettano. Cio' che di una bozza si legge — rilievi,
 * contenuto, due scale, soglie — sta sotto un pannello che si apre nella tappa
 * del pacchetto, perche' la pagina resti una pagina.
 *
 * **Offline non muta.** Congelare, campionare, sigillare, comporre, validare e
 * inviare sono atti verso il committente: una scrittura in coda locale sarebbe
 * una consegna che il servente non ha mai visto.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ServiceError } from '../api'
import { Hint } from '../carta/Hint'
import type { Concept } from '../i18n/explain'
import {
  EXPORT_COMPOSE,
  EXPORT_SEND,
  EXPORT_VALIDATE,
  QUALITY_SAMPLE,
  QUALITY_SEAL,
  REVIEW_FREEZE,
  type Whoami,
} from '../auth'
import * as exports from '../exports'
import { decimal } from '../format'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import { newKey } from '../api'
import * as privacy from '../privacy'
import type { PrivacyState } from '../privacy'
import * as quality from '../quality'
import type { QualityProgress, QualitySeal } from '../quality'
import type { Draft, Item } from '../types'
import {
  Blockers,
  Contents,
  Metrics,
  PermanentReceipt,
  Scales,
  Summary,
  Thresholds,
  withdrawn,
} from './parts'
import './delivery.css'

export const CONSEGNA = 'modulo-consegna'

export interface DeliveryViewProps {
  inspectionId: string
  who?: Whoami | null
}

type StepId = 'review' | 'freeze' | 'sample' | 'seal' | 'privacy' | 'package' | 'validate' | 'send' | 'receipt'
type Action = 'freeze' | 'unfreeze' | 'sample' | 'seal' | 'privacy' | 'compose' | 'validate' | 'submit' | 'report'

/* Il passo porta con se' la parola del dizionario che nomina (GI-281): il
 * supervisore incontra «congelata», «campione» e «sigillo» per la prima volta
 * qui, e il segno «?» sta dove sta la parola. */
const STEPS: { id: StepId; title: MessageKey; about?: Concept }[] = [
  { id: 'review', title: 'delivery.step.review' },
  { id: 'freeze', title: 'delivery.step.freeze', about: 'freeze' },
  { id: 'sample', title: 'delivery.step.sample', about: 'sample' },
  { id: 'seal', title: 'delivery.step.seal', about: 'seal' },
  { id: 'privacy', title: 'delivery.step.privacy' },
  { id: 'package', title: 'delivery.step.package', about: 'delivery' },
  { id: 'validate', title: 'delivery.step.validate' },
  { id: 'send', title: 'delivery.step.send' },
  { id: 'receipt', title: 'delivery.step.receipt' },
]

function memory(inspectionId: string): string {
  return `gridfly.export.${inspectionId}`
}

function may(who: Whoami | null | undefined, action: string): boolean {
  return who == null || who.may.includes(action)
}

const SENT = new Set(['submitted', 'accepted', 'rejected'])

export function DeliveryView({ inspectionId, who = null }: DeliveryViewProps): JSX.Element {
  const t = useT()
  const [progress, setProgress] = useState<QualityProgress | null>(null)
  const [seal, setSeal] = useState<QualitySeal | 'missing'>('missing')
  const [scan, setScan] = useState<PrivacyState | null>(null)
  const [known, setKnown] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [contents, setContents] = useState<Item[] | null>(null)
  const [twoScales, setTwoScales] = useState<exports.Scales | null>(null)
  const [note, setNote] = useState('')
  const [file, setFile] = useState<string | null>(null)
  const [failure, setFailure] = useState<ServiceError | null>(null)
  const [busy, setBusy] = useState<Action | null>(null)
  const [online, setOnline] = useState(window.navigator.onLine)

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  /** Rilegge l'avanzamento e il sigillo: e' lo stato delle prime quattro tappe. */
  const readState = useCallback(async (): Promise<void> => {
    const found = await quality.readProgress(inspectionId)
    setProgress(found)
    if (found.seal_valid) {
      try {
        const got = await quality.readSeal(inspectionId)
        setSeal(got.valid ? got : 'missing')
      } catch {
        setSeal('missing')
      }
    } else {
      setSeal('missing')
    }
    // L'anonimizzazione non blocca la lettura del resto: se non si legge, la
    // tappa dice «non letto» invece di togliere la pagina.
    try {
      setScan(await privacy.state(inspectionId))
    } catch {
      setScan(null)
    }
  }, [inspectionId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await readState()
      } catch (error: unknown) {
        if (!cancelled && error instanceof ServiceError) setFailure(error)
      }
      if (cancelled) return
      // La bozza in corso: quella ricordata dalla scheda, altrimenti l'ultima
      // consegna partita per questa commessa, che e' la ricevuta da mostrare.
      const remembered = window.sessionStorage.getItem(memory(inspectionId))
      try {
        if (remembered !== null) {
          const found = await exports.show(remembered)
          if (!cancelled) setDraft(found)
        } else {
          const sent = (await exports.sent()).filter((one) => one.inspection_id === inspectionId)
          const last = sent[0]
          if (last && !cancelled) setDraft(await exports.show(last.export_id))
        }
      } catch {
        window.sessionStorage.removeItem(memory(inspectionId))
      }
      if (!cancelled) setKnown(true)
    })()
    return () => {
      cancelled = true
    }
  }, [inspectionId, readState])

  function run(action: Action, work: () => Promise<unknown>): void {
    if (!online) return
    setBusy(action)
    setFailure(null)
    work()
      .then(async (result) => {
        if (action === 'compose' || action === 'validate' || action === 'submit') {
          const found = result as Draft
          setDraft(found)
          window.sessionStorage.setItem(memory(inspectionId), found.export_id)
          setContents(null)
          setTwoScales(null)
        }
        await readState()
      })
      .catch((error: unknown) => {
        if (error instanceof ServiceError) setFailure(error)
      })
      .finally(() => setBusy(null))
  }

  const offline = !online
  const live = draft !== null && !withdrawn(draft, seal)
  const sent = live && SENT.has(draft.state)
  const sampleDone =
    progress !== null &&
    progress.sample_id !== null &&
    progress.sample_required !== null &&
    (progress.sample_reviewed ?? 0) >= progress.sample_required

  const done: Record<StepId, boolean> = {
    review: progress !== null && progress.waiting === 0 && progress.conflicts === 0,
    freeze: progress?.frozen === true,
    sample: sampleDone,
    seal: progress?.seal_valid === true && seal !== 'missing',
    privacy: scan?.complete === true,
    package: live && draft.errors.length === 0,
    validate: live && draft.state !== 'draft',
    send: sent,
    receipt: sent,
  }
  const current: StepId | null = STEPS.find((one) => !done[one.id])?.id ?? null
  const stateOf = (id: StepId): 'done' | 'current' | 'todo' =>
    done[id] ? 'done' : id === current ? 'current' : 'todo'

  const errors = draft?.errors.length ?? 0
  const warnings = draft?.warnings.length ?? 0
  const base = `/inspections/${inspectionId}`

  return (
    <main className="delivery-view" id={CONSEGNA}>
      {offline && <p role="status">{t('delivery.offline')}</p>}
      {failure && (
        <p className="avviso" role="alert">
          {failure.message}
          {failure.requestId && <small> · {t('pool.requestId', { id: failure.requestId })}</small>}
        </p>
      )}
      {!known && <p role="status">{t('delivery.reading')}</p>}

      {/* La pagina che si legge e si stampa, accanto al pacchetto che legge
          una macchina (GI-258). Si prende con le credenziali e si apre in
          una scheda: un collegamento nudo non le porterebbe. */}
      <p className="delivery-actions delivery-report">
        <button
          type="button"
          disabled={offline || busy !== null}
          onClick={() =>
            run('report', async () => {
              const page = await exports.report(inspectionId)
              window.open(URL.createObjectURL(page), '_blank', 'noopener')
            })
          }
        >
          {t('delivery.openReport')}
        </button>
        <span className="tenue"> {t('delivery.reportNote')}</span>
      </p>

      {/*
        Il filo delle tappe in una riga (S17, L-5): ● fatte, ○ dopo, ∥ in
        parallelo, la tappa che tocca adesso in evidenza. Decorativo: le tappe
        vere stanno sotto, coi loro nomi e le loro azioni.
      */}
      <p className="delivery-stepper" aria-hidden="true">
        {STEPS.map((step) => {
          const state = stateOf(step.id)
          return (
            <span key={step.id} className="delivery-stepper-step" data-state={state} data-parallel={step.id === 'privacy' || undefined}>
              <span className="delivery-stepper-mark">{state === 'done' ? '●' : step.id === 'privacy' ? '∥' : '○'}</span>
              <span className="delivery-stepper-name">{t(step.title)}</span>
            </span>
          )
        })}
      </p>

      <ol className="delivery-steps">
        {STEPS.map((step) => {
          const state = stateOf(step.id)
          // L'anonimizzazione non sta nella fila: corre in parallelo e puo'
          // finire prima delle tappe che la precedono nell'elenco. Un numero
          // prometterebbe un ordine che non c'e' (GI-256).
          const parallel = step.id === 'privacy'
          const number = STEPS.filter((one) => one.id !== 'privacy').findIndex((one) => one.id === step.id) + 1
          return (
            <li key={step.id} className="delivery-step" data-state={state} data-parallel={parallel || undefined}>
              <span className="delivery-mark" aria-hidden="true">
                {state === 'done' ? '✓' : parallel ? '∥' : number}
              </span>
              <div className="delivery-body">
                <h3>
                  {t(step.title)}
                  {step.about !== undefined && <Hint about={step.about} />}
                  {parallel && <span className="tenue delivery-parallel"> · {t('delivery.parallel')}</span>}
                  <span className="solo-lettori">
                    {' '}
                    · {t(state === 'done' ? 'delivery.done' : state === 'current' ? 'delivery.now' : 'delivery.later')}
                  </span>
                </h3>

                {step.id === 'review' && progress !== null && (
                  <>
                    <p className="delivery-line t-num">
                      {t('delivery.reviewLine', { waiting: decimal(progress.waiting), decided: decimal(progress.decided) })}
                      {progress.conflicts > 0 && <> · {t('delivery.conflicts', { n: progress.conflicts })}</>}
                      {progress.coverage_gaps > 0 && <> · {t('delivery.gaps', { n: progress.coverage_gaps })}</>}
                    </p>
                    <p className="delivery-links">
                      {progress.waiting > 0 && <Link to={`${base}/anomalies?status=waiting`}>{t('delivery.openAnomalies')}</Link>}
                      {progress.coverage_gaps > 0 && <Link to={`${base}/coverage`}>{t('delivery.openCoverage')}</Link>}
                    </p>
                  </>
                )}

                {step.id === 'freeze' && progress !== null && (
                  <>
                    <p className="delivery-line t-num">
                      {progress.frozen
                        ? t('delivery.frozenLine', {
                            epoch: progress.review_epoch,
                            freeze: progress.frozen_review_version ?? '',
                            supervisor: progress.supervisor_review_version ?? '',
                          })
                        : t('delivery.notFrozen')}
                    </p>
                    <p className="delivery-actions">
                      {!progress.frozen && (
                        <button
                          type="button"
                          className="primario"
                          disabled={offline || busy !== null || state !== 'current' || !may(who, REVIEW_FREEZE)}
                          onClick={() => run('freeze', () => quality.freezeReview(inspectionId))}
                        >
                          {t('delivery.freeze')}
                        </button>
                      )}
                      {progress.frozen && !sent && (
                        <button
                          type="button"
                          disabled={offline || busy !== null || !may(who, REVIEW_FREEZE)}
                          onClick={() => run('unfreeze', () => quality.unfreezeReview(inspectionId))}
                        >
                          {t('delivery.unfreeze')}
                        </button>
                      )}
                    </p>
                  </>
                )}

                {step.id === 'sample' && progress !== null && (
                  <>
                    <p className="delivery-line t-num">
                      {progress.sample_id === null
                        ? t('delivery.noSample')
                        : t('delivery.sampleLine', {
                            reviewed: progress.sample_reviewed ?? 0,
                            required: progress.sample_required ?? 0,
                          })}
                    </p>
                    <p className="delivery-actions">
                      {progress.sample_id === null ? (
                        <button
                          type="button"
                          className="primario"
                          disabled={offline || busy !== null || state !== 'current' || !may(who, QUALITY_SAMPLE)}
                          onClick={() => run('sample', () => quality.createSample(inspectionId))}
                        >
                          {t('delivery.createSample')}
                        </button>
                      ) : (
                        !sampleDone && <Link to={`${base}/quality`}>{t('delivery.reviewSample')}</Link>
                      )}
                    </p>
                  </>
                )}

                {step.id === 'seal' && progress !== null && (
                  <>
                    <p className="delivery-line t-num">
                      {seal !== 'missing'
                        ? t('delivery.sealLine', {
                            epoch: seal.review_epoch,
                            freeze: seal.frozen_review_version,
                            supervisor: seal.supervisor_review_version,
                          })
                        : t('delivery.noSealYet')}
                    </p>
                    {seal === 'missing' && (
                      <p className="delivery-actions">
                        <button
                          type="button"
                          className="primario"
                          disabled={
                            offline || busy !== null || state !== 'current' || progress.sample_id === null || !may(who, QUALITY_SEAL)
                          }
                          onClick={() =>
                            run('seal', () => quality.applySeal(inspectionId, progress.sample_id ?? ''))
                          }
                        >
                          {t('delivery.applySeal')}
                        </button>
                      </p>
                    )}
                  </>
                )}

                {step.id === 'privacy' && (
                  <>
                    <p className="delivery-line t-num">
                      {scan === null
                        ? t('privacy.unread')
                        : t('delivery.privacyLine', { n: scan.scanned, total: scan.photos })}
                      {scan !== null && scan.scanned > 0 && !scan.complete && <> · {t('delivery.privacyPending')}</>}
                    </p>
                    {!sent && (
                      <p className="delivery-actions">
                        <button
                          type="button"
                          className={state === 'current' ? 'primario' : undefined}
                          disabled={offline || busy !== null || !may(who, EXPORT_COMPOSE)}
                          onClick={() => run('privacy', () => privacy.startScan(inspectionId, newKey()))}
                        >
                          {scan !== null && scan.scanned > 0 ? t('delivery.rescan') : t('delivery.scan')}
                        </button>
                      </p>
                    )}
                  </>
                )}

                {step.id === 'package' && (
                  <>
                    <p className="delivery-line">
                      {draft === null
                        ? t('delivery.noPackage')
                        : !live
                          ? t('delivery.packageWithdrawn')
                          : errors > 0
                            ? t('delivery.closedBy', { n: errors })
                            : warnings > 0
                              ? t('delivery.warningsOpen', { n: warnings })
                              : t('delivery.noIssues')}
                    </p>
                    {live && draft.not_deliverable_findings > 0 && (
                      <p className="avviso">{t('delivery.undeliverable', { n: draft.not_deliverable_findings })}</p>
                    )}
                    {!sent && (
                      <p className="delivery-actions">
                        <button
                          type="button"
                          className="primario"
                          disabled={
                            offline || busy !== null || seal === 'missing' || (state !== 'current' && !live) || !may(who, EXPORT_COMPOSE)
                          }
                          onClick={() => run('compose', () => exports.compose(inspectionId))}
                        >
                          {live ? t('delivery.recompose') : t('delivery.compose')}
                        </button>
                      </p>
                    )}
                    {live && (
                      <details className="delivery-details">
                        <summary>{t('delivery.showPackage')}</summary>
                        <Summary draft={draft} />
                        <Blockers draft={draft} />
                        <p>
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => {
                              void exports.items(draft.export_id).then(setContents).catch((error: unknown) => {
                                if (error instanceof ServiceError) setFailure(error)
                              })
                              void exports.scales(draft.export_id).then(setTwoScales).catch((error: unknown) => {
                                if (error instanceof ServiceError) setFailure(error)
                              })
                            }}
                          >
                            {t('delivery.whatLeft')}
                          </button>
                        </p>
                        {contents && <Contents items={contents} />}
                        {twoScales && <Scales scales={twoScales} />}
                        {draft.thresholds.length > 0 && <Thresholds thresholds={draft.thresholds} />}
                        {draft.metrics && <Metrics metrics={draft.metrics} />}
                      </details>
                    )}
                  </>
                )}

                {step.id === 'validate' && (
                  <>
                    <p className="delivery-line">
                      {live && draft.state !== 'draft' ? t('delivery.validated') : t('delivery.notValidated')}
                    </p>
                    {live && draft.state === 'draft' && errors === 0 && (
                      <p className="delivery-actions">
                        <button
                          type="button"
                          className="primario"
                          disabled={
                            offline || busy !== null || state !== 'current' || !draft.deliverable || !may(who, EXPORT_VALIDATE)
                          }
                          onClick={() => run('validate', () => exports.validate(draft.export_id))}
                        >
                          {t('delivery.validate')}
                        </button>
                      </p>
                    )}
                  </>
                )}

                {step.id === 'send' && (
                  <>
                    <p className="delivery-line">
                      {sent
                        ? t('delivery.sentLine', { who: draft.sent_by ?? '' })
                        : warnings > 0 && live
                          ? t('delivery.warningsOpen', { n: warnings })
                          : t('delivery.notSent')}
                    </p>
                    {live && draft.state === 'validated' && (
                      <>
                        {warnings > 0 && (
                          <p>
                            <label>
                              {t('delivery.whyWarnings')}
                              <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} />
                            </label>
                          </p>
                        )}
                        <p className="delivery-actions">
                          <button
                            type="button"
                            className="primario"
                            disabled={
                              offline || busy !== null || (warnings > 0 && !note.trim()) || !may(who, EXPORT_SEND)
                            }
                            onClick={() => run('submit', () => exports.submit(draft.export_id, note.trim() || null))}
                          >
                            {t('delivery.registerSend')}
                          </button>
                        </p>
                      </>
                    )}
                  </>
                )}

                {step.id === 'receipt' &&
                  (sent ? (
                    <PermanentReceipt
                      draft={draft}
                      file={file}
                      onDocument={() =>
                        void exports
                          .document(draft.export_id)
                          .then((body) => {
                            const blob = new Blob([JSON.stringify(body, null, 1)], { type: 'application/json' })
                            setFile(URL.createObjectURL(blob))
                          })
                          .catch((error: unknown) => {
                            if (error instanceof ServiceError) setFailure(error)
                          })
                      }
                    />
                  ) : (
                    <p className="delivery-line">{t('delivery.noReceipt')}</p>
                  ))}
              </div>
            </li>
          )
        })}
      </ol>
    </main>
  )
}
